/**
 * Agent Main Area Component
 * 
 * Main content area for the agent workspace.
 * Switches between:
 * - Agent Hub view (all agents grid)
 * - Agent Chat Container (selected agent's landing/chat)
 * - AI Workbench (iframe URL from config)
 */
import { Component, ElementRef, HostListener, Input, Output, EventEmitter, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LoggerService } from '../../../../../services/logger-services/logger.service';
import { ConfigurationService } from '../../../../../services/configuration/configuration.service';
import { RegulationNextService } from '../../../../../services/regulation-next/regulation-next.service';
import { AgentHubItem } from '../../../models';
import { FirstTimeLoginDetails } from '../../../../models/chatbot.models';
import { AI_WORKBENCH_AGENT_ID } from '../../../services/agent-orchestrator/agent-orchestrator.service';
import {
  WORKBENCH_CHATBOT_CONTEXT_TYPE,
  WORKBENCH_CHATBOT_CONTEXT_VERSION,
  WORKBENCH_CHILD_MESSAGE_SOURCE,
  WORKBENCH_PARENT_MESSAGE_SOURCE,
  WORKBENCH_READY_POST_MESSAGE_TYPE,
  WorkbenchChatbotContext,
  WorkbenchChatbotContextMessage
} from '../../../constants/workbench-post-message';

@Component({
  selector: 'app-agent-main-area',
  templateUrl: './main-area.component.html',
  styleUrls: ['./main-area.component.scss']
})
export class AgentMainAreaComponent implements OnChanges, OnInit, OnDestroy {

  readonly aiWorkbenchAgentId = AI_WORKBENCH_AGENT_ID;

  /** Sanitized iframe src when on AI Workbench */
  safeAiWorkbenchUrl: SafeResourceUrl | null = null;

  /** Live ref to the iframe element so we can re-derive `contentWindow` on demand
   *  (caching a Window is fragile — about:blank → real-URL transitions and Angular
   *  re-renders can swap the underlying window). */
  @ViewChild('aiWorkbenchIframe') private aiWorkbenchIframe?: ElementRef<HTMLIFrameElement>;

  /** True once the iframe's `load` event has fired for the actual workbench URL,
   *  i.e. after the post-load handshake succeeded once. Used to skip the
   *  initial synthetic `about:blank` load (which inherits the parent's origin
   *  and would emit a cross-origin postMessage warning). */
  private workbenchIframeLoaded = false;

  /** Cleanup for subscriptions (TranslateService.onLangChange). */
  private readonly destroy$ = new Subject<void>();

  /** Whether to show the agent hub (all agents view) */
  @Input() isAgentHubMode: boolean = true;

  /** Current agent ID when in agent mode */
  @Input() currentAgentId: string = '';

  /** App-config workbench flags ∩ privilege*/
  @Input() workbenchAccessAllowed = false;

  /** Mirrors Workbench context `aiAssistant` (true when opened via ?route=workbench). */
  @Input() workbenchAiAssistant = false;

  /** Whether sidebar is open */
  @Input() isSidebarOpen: boolean = true;

  /** Whether loader should be shown */
  @Input() loaderShow: boolean = false;

  /** User's first name for personalization */
  @Input() userFName: string = '';

  /** Reference to parent chatbot component for callbacks */
  @Input() chatbotComponentRef: any;

  /** Emitted when an agent is selected from the hub */
  @Output() agentSelected = new EventEmitter<string>();

  /** Emitted when user wants to navigate to hub */
  @Output() navigateToHub = new EventEmitter<void>();

  /** Emitted when sidebar toggle is requested */
  @Output() toggleSidebar = new EventEmitter<void>();

  /** Emitted when source panel opens and left sidebar should close */
  @Output() requestCloseSidebar = new EventEmitter<void>();

  constructor(
    private readonly logger: LoggerService,
    private sanitizer: DomSanitizer,
    private configurationService: ConfigurationService,
    private readonly regulationNextService: RegulationNextService,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    // Push a delta to the Workbench iframe whenever the chatbot switches
    // locale. Uses the same `FREYA_CHATBOT_CONTEXT` envelope as the initial
    // snapshot — just with only the `language` key populated.
    //
    // We re-derive the iframe's contentWindow on every emission rather than
    // caching it, because the iframe element can be torn down and recreated
    // by *ngIf when the user toggles workbench access.
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ lang }) => {
        if (this.currentAgentId !== AI_WORKBENCH_AGENT_ID || !this.workbenchAccessAllowed) {
          return;
        }
        if (!this.workbenchIframeLoaded) {
          // Iframe hasn't completed its real load yet; the upcoming iframe-load
          // snapshot will carry the new language so we don't need to re-send here.
          return;
        }
        const win = this.aiWorkbenchIframe?.nativeElement?.contentWindow;
        const targetOrigin = this.getAiWorkbenchOriginForPostMessage();
        if (!win || !targetOrigin) {
          return;
        }
        this.postContextToWorkbench(
          win,
          targetOrigin,
          { language: lang },
          `onLangChange:${lang}`
        );
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentAgentId'] || changes['workbenchAccessAllowed']) {
      this.updateAiWorkbenchIframeSrc();
    }
  }

  private updateAiWorkbenchIframeSrc(): void {
    if (this.currentAgentId !== AI_WORKBENCH_AGENT_ID || !this.workbenchAccessAllowed) {
      this.safeAiWorkbenchUrl = null;
      // Iframe is being torn down — reset the load gate so the next mount waits
      // for a fresh load before posting.
      this.workbenchIframeLoaded = false;
      return;
    }
    const raw = this.configurationService.config?.AIWorkbenchUrl;
    if (raw && typeof raw === 'string') {
      this.safeAiWorkbenchUrl = this.sanitizer.bypassSecurityTrustResourceUrl(raw);
    } else {
      this.safeAiWorkbenchUrl = null;
      this.logger.logInfo('[AgentMainArea] AIWorkbenchUrl missing from config', 'updateAiWorkbenchIframeSrc');
    }
  }

  /**
   * After the Workbench iframe document loads, send a full snapshot of the
   * chatbot's shell context (regulation-next access, current language, …) so
   * the workbench can mirror it.
   *
   * Note: the iframe's `load` event can fire for the initial about:blank state
   * (where the iframe inherits the parent's origin); a postMessage to
   * `AIWorkbenchUrl` origin will be silently rejected by the browser with a
   * cross-origin warning. We detect that pre-navigation state by checking
   * whether the iframe currently has a usable src attribute.
   */
  onAiWorkbenchIframeLoad(event: Event): void {
    const iframe = event.target as HTMLIFrameElement | null;
    const win = iframe?.contentWindow;
    if (!win) {
      this.logger.logInfo('[AgentMainArea] Workbench iframe load: no contentWindow — skip postMessage', 'onAiWorkbenchIframeLoad');
      return;
    }
    if (!this.isIframeLoadForRealUrl(iframe)) {
      this.logger.logInfo('[AgentMainArea] Skipping Workbench postMessage — iframe is still about:blank', 'onAiWorkbenchIframeLoad');
      return;
    }
    const targetOrigin = this.getAiWorkbenchOriginForPostMessage();
    if (!targetOrigin) {
      this.logger.logInfo('[AgentMainArea] Skipping Workbench postMessage — could not resolve AIWorkbenchUrl origin', 'onAiWorkbenchIframeLoad');
      return;
    }
    this.workbenchIframeLoaded = true;
    this.postContextToWorkbench(win, targetOrigin, this.buildChatbotContextSnapshot(), 'iframe load');
  }

  /**
   * `load` fires for the implicit `about:blank` state before the iframe actually
   * navigates to its `src`. In that state `iframe.src` is the empty string (or
   * `about:blank`) and posting to the configured AIWorkbenchUrl origin would
   * trigger a benign-but-noisy "target origin doesn't match recipient" warning.
   */
  private isIframeLoadForRealUrl(iframe: HTMLIFrameElement | null): boolean {
    if (!iframe) {
      return false;
    }
    const src = iframe.getAttribute('src') ?? '';
    if (!src || src === 'about:blank') {
      return false;
    }
    return true;
  }

  /**
   * Workbench sends `FREYA_WORKBENCH_READY` after React mounts so we re-send a
   * full `FREYA_CHATBOT_CONTEXT` snapshot (the iframe `load` postMessage can
   * fire before the child's listener exists).
   */
  @HostListener('window:message', ['$event'])
  onWorkbenchWindowMessage(event: MessageEvent): void {
    if (this.currentAgentId !== AI_WORKBENCH_AGENT_ID || !this.workbenchAccessAllowed) {
      return;
    }
    if (!this.allowedWorkbenchPostMessageOrigins().has(event.origin)) {
      return;
    }
    const data = event.data;
    if (!data || typeof data !== 'object') {
      return;
    }
    if (
      (data as { type?: string }).type !== WORKBENCH_READY_POST_MESSAGE_TYPE ||
      (data as { source?: string }).source !== WORKBENCH_CHILD_MESSAGE_SOURCE
    ) {
      return;
    }
    const sourceWin = event.source;
    if (!sourceWin || sourceWin === window) {
      return;
    }
    const win = sourceWin as Window;
    if (typeof win.postMessage !== 'function') {
      return;
    }

    this.workbenchIframeLoaded = true;
    this.postContextToWorkbench(
      win,
      event.origin,
      this.buildChatbotContextSnapshot(),
      'FREYA_WORKBENCH_READY handshake'
    );
  }

  /**
   * Allowed origins for Workbench → shell postMessage (same rules as AgentWorkspaceComponent).
   */
  private allowedWorkbenchPostMessageOrigins(): Set<string> {
    const origins = new Set<string>();
    const cfg = this.configurationService.config;

    const addOriginString = (value: string): void => {
      const v = value.trim();
      if (!v) {
        return;
      }
      try {
        origins.add(new URL(v).origin);
      } catch {
        /* ignore invalid */
      }
    };

    const raw = cfg?.AIWorkbenchUrl;
    if (raw && typeof raw === 'string') {
      addOriginString(raw);
    }

    const extra = cfg?.AIWorkbenchPostMessageAllowedOrigins;
    if (Array.isArray(extra)) {
      for (const o of extra) {
        if (typeof o === 'string') {
          addOriginString(o);
        }
      }
    }

    return origins;
  }

  /**
   * Build a full snapshot of the chatbot's shell context for the workbench.
   * Used on iframe load and on the `FREYA_WORKBENCH_READY` handshake.
   *
   * Add new keys to `WorkbenchChatbotContext` and read them here as the shell
   * grows new shared state — the rest of the postMessage plumbing stays the
   * same.
   */
  private buildChatbotContextSnapshot(): Partial<WorkbenchChatbotContext> {
    // Falls back to default lang (set by LanguageChangeService → 'en') if
    // `use()` hasn't resolved yet; the workbench expects a non-empty locale.
    const language = this.translate.currentLang || this.translate.getDefaultLang?.() || 'en';
    return {
      isRegulationsNext: this.regulationNextService.hasRegulationNextAccess(),
      language,
      aiAssistant: this.workbenchAiAssistant
    };
  }

  /**
   * Single sender for the `FREYA_CHATBOT_CONTEXT` envelope. `context` may be a
   * full snapshot or a partial delta (e.g. only `{ language }`).
   */
  private postContextToWorkbench(
    win: Window,
    targetOrigin: string,
    context: Partial<WorkbenchChatbotContext>,
    trace: string
  ): void {
    const payload: WorkbenchChatbotContextMessage = {
      type: WORKBENCH_CHATBOT_CONTEXT_TYPE,
      source: WORKBENCH_PARENT_MESSAGE_SOURCE,
      version: WORKBENCH_CHATBOT_CONTEXT_VERSION,
      context
    };
    try {
      win.postMessage(payload, targetOrigin);
      this.logger.logInfo(
        `[AgentMainArea] Posted chatbot context to Workbench (${trace}, origin=${targetOrigin}, keys=[${Object.keys(context).join(',')}])`,
        'postContextToWorkbench'
      );
    } catch (e: any) {
      this.logger.logInfo(
        `[AgentMainArea] postMessage context to Workbench failed (${trace}): ${e?.message ?? e}`,
        'postContextToWorkbench'
      );
    }
  }

  private getAiWorkbenchOriginForPostMessage(): string | null {
    const raw = this.configurationService.config?.AIWorkbenchUrl;
    if (!raw || typeof raw !== 'string') {
      return null;
    }
    try {
      return new URL(raw).origin;
    } catch {
      return null;
    }
  }

  /**
   * Handle agent card click from hub
   */
  onAgentCardClicked(agent: AgentHubItem): void {
    this.logger.logInfo(`[AgentMainArea] Agent selected: ${agent.id}`, 'onAgentCardClicked');
    this.agentSelected.emit(agent.id);
  }

  /**
   * Handle navigation to hub request
   */
  onNavigateToHub(): void {
    this.navigateToHub.emit();
  }

  /**
   * Handle sidebar toggle from floating menu
   */
  onToggleSidebar(): void {
    this.toggleSidebar.emit();
  }
}
